import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  useCustomerNotes,
  useCreateCustomerNote,
  useUpdateCustomerNote,
  useDeleteCustomerNote,
} from '@/hooks/useCustomerNotes';
import { Skeleton } from './Skeleton';
import { EmptyState } from './EmptyState';
import type { CustomerNote } from '@/lib/types';

interface CustomerNotesProps {
  accountId: string;
  customerEmail: string;
  customerName?: string;
  userId: string; // Current user ID for creating notes
}

export default function CustomerNotes({
  accountId,
  customerEmail,
  customerName,
  userId,
}: CustomerNotesProps) {
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);

  const { data: notes, isLoading, error } = useCustomerNotes(accountId, customerEmail);
  const createNoteMutation = useCreateCustomerNote(accountId, customerEmail);
  const updateNoteMutation = useUpdateCustomerNote(accountId, customerEmail);
  const deleteNoteMutation = useDeleteCustomerNote(accountId, customerEmail);

  const handleCreateNote = () => {
    if (!newNote.trim()) return;

    createNoteMutation.mutate(
      {
        account_id: accountId,
        customer_name: customerName,
        note: newNote.trim(),
        user_id: userId,
      },
      {
        onSuccess: () => {
          setNewNote('');
        },
      }
    );
  };

  const handleUpdateNote = (noteId: string) => {
    if (!editingText.trim()) return;

    updateNoteMutation.mutate(
      { id: noteId, note: editingText.trim() },
      {
        onSuccess: () => {
          setEditingNoteId(null);
          setEditingText('');
        },
      }
    );
  };

  const handleDeleteNote = (noteId: string) => {
    setDeletingNoteId(noteId);
  };

  const confirmDelete = () => {
    if (deletingNoteId) {
      deleteNoteMutation.mutate(deletingNoteId, {
        onSettled: () => {
          setDeletingNoteId(null);
        },
      });
    }
  };

  const cancelDelete = () => {
    setDeletingNoteId(null);
  };

  const startEditing = (note: CustomerNote) => {
    setEditingNoteId(note.id);
    setEditingText(note.note);
  };

  const cancelEditing = () => {
    setEditingNoteId(null);
    setEditingText('');
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-800">
          Failed to load customer notes. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-sm font-semibold">Customer Notes</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Add context for AI-powered drafts
        </p>
      </div>

      {/* New Note Input */}
      <div className="space-y-2">
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Add a note about this customer..."
          className="w-full min-h-[80px] p-2 text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <button
          onClick={handleCreateNote}
          disabled={!newNote.trim() || createNoteMutation.isPending}
          className="w-full px-3 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {createNoteMutation.isPending ? 'Saving...' : 'Save Note'}
        </button>
      </div>

      {/* Notes List */}
      {notes && notes.length > 0 ? (
        <div className="space-y-3">
          <div className="text-xs font-semibold text-muted-foreground uppercase">
            Previous Notes ({notes.length})
          </div>
          {notes.map((note) => (
            <div
              key={note.id}
              className="rounded-lg border bg-card p-3 space-y-2"
            >
              {deletingNoteId === note.id ? (
                // Delete confirmation mode
                <div className="space-y-2">
                  <p className="text-sm font-medium text-red-600">
                    Delete this note?
                  </p>
                  <p className="text-xs text-muted-foreground">
                    This action cannot be undone.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={confirmDelete}
                      disabled={deleteNoteMutation.isPending}
                      className="flex-1 px-2 py-1 text-xs font-medium rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {deleteNoteMutation.isPending ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      onClick={cancelDelete}
                      disabled={deleteNoteMutation.isPending}
                      className="flex-1 px-2 py-1 text-xs font-medium rounded border hover:bg-muted"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : editingNoteId === note.id ? (
                // Edit mode
                <div className="space-y-2">
                  <textarea
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    className="w-full min-h-[60px] p-2 text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleUpdateNote(note.id)}
                      disabled={!editingText.trim() || updateNoteMutation.isPending}
                      className="flex-1 px-2 py-1 text-xs font-medium rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                      {updateNoteMutation.isPending ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={cancelEditing}
                      className="flex-1 px-2 py-1 text-xs font-medium rounded border hover:bg-muted"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View mode
                <>
                  <p className="text-sm">{note.note}</p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      {formatDistanceToNow(new Date(note.created_at), {
                        addSuffix: true,
                      })}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEditing(note)}
                        className="text-primary hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon="📝"
          title="No notes yet"
          description="Add notes to help AI understand this customer better"
        />
      )}
    </div>
  );
}
